import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { TableEntry } from './TableEntry';
import { MesaEntry } from './MesaEntry';
import { MesaTableEntry } from './MesaTableEntry';
import AdminLayout from '../features/admin/dashboard/AdminLayout';
import AdminDashboardHome from '../features/admin/dashboard/AdminDashboardHome';
import Login from '../features/auth/screens/Login';
import PrivateRoute from '../features/auth/components/PrivateRoute';
import MigrateMesas from '../legacy/tools/MigrateMesas';
import RestaurantDetailScreen from '../features/super-admin/screens/RestaurantDetailScreen';
import AdminMesasScreen from '../features/admin/mesas/AdminMesasScreen';
import QRCodesScreen from '../features/admin/mesas/QRCodesScreen';
import MesaSummaryScreen from '../features/admin/mesas/MesaSummaryScreen';
import MesaQRCodeScreen from '../features/admin/mesas/MesaQRCodeScreen';
import MesaTransactionsScreen from '../features/admin/mesas/MesaTransactionsScreen';
import TipSelectionScreen from '../features/customer/screens/TipSelectionScreen';
import AzulCheckoutScreen from '../features/customer/payment/screens/AzulCheckoutScreen';
import StripeCheckoutScreen from '../features/customer/payment/screens/StripeCheckoutScreen';
import PaymentProcessingScreen from '../features/customer/payment/screens/PaymentProcessingScreen';
import PaymentBreakdownScreen from '../features/customer/screens/PaymentBreakdownScreen';
import CardNetCheckoutScreen from '../features/customer/payment/screens/CardNetCheckoutScreen';
import PaymentSuccessScreen from '../features/customer/screens/PaymentSuccessScreen';
import PaymentSuccessMailOptionScreen from '../features/customer/screens/PaymentSuccessMailOptionScreen';
import ThankYouGoodbyeScreen from '../features/customer/screens/ThankYouGoodbyeScreen';
import PosPaymentSuccess from '../features/customer/payment/screens/PaymentSuccess';
import SettingScreen from '../features/admin/settings/SettingScreen';
import POSConfigScreen from '../features/admin/settings/POSConfigScreen';
import PagosScreen from '../features/admin/pagos/PagosScreen';

// Super Admin imports
import SuperAdminLayout from '../features/super-admin/SuperAdminLayout';
import SuperAdminDashboard from '../features/super-admin/SuperAdminDashboard';
import CreateRestaurantScreen from '../features/super-admin/screens/CreateRestaurantScreen';
import RestaurantListScreen from '../features/super-admin/screens/RestaurantListScreen';
import MigrationToolScreen from '../features/super-admin/MigrationToolScreen';
import RestaurantStatisticsScreen from '../features/super-admin/screens/RestaurantStatisticsScreen';
import RestaurantTransactionsScreen from '../features/super-admin/screens/RestaurantTransactionsScreen';
import { useRestaurant } from '../providers/RestaurantContext';
import { UserRole } from '../types/restaurant';

// Utility function to get default route based on user role
const getDefaultRouteForRole = (role: UserRole | null): string => {
  switch (role) {
    case 'super_admin':
      return '/super-admin';  // ✅ ADDED
    case 'owner':
    case 'admin':
      return '/admin';
    case 'staff':
      return '/mesas';
    default:
      return '/login';  // ✅ CHANGED from '/admin'
  }
};

const AppRoutes: React.FC = () => {
  const { userRole } = useRestaurant();

  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/login" element={<Login />} />
      <Route path="/" element={<Navigate to={getDefaultRouteForRole(userRole)} replace />} /> {/* ✅ CHANGED */}

      {/* Customer Routes - PUBLIC (no login required) */}
      <Route path="/mesa/:mesaId" element={<MesaTableEntry />} />
      <Route path="/t/:ticketId" element={<TableEntry />} />
      <Route path="/tip-selection" element={<TipSelectionScreen />} />
      <Route path="/pos-payment-success" element={<PosPaymentSuccess />} />
      <Route path="/azul-checkout" element={<AzulCheckoutScreen />} />
      <Route path="/stripe-checkout" element={<StripeCheckoutScreen />} />
      <Route path="/cardnet-checkout" element={<CardNetCheckoutScreen />} />
      <Route path="/payment-processing" element={<PaymentProcessingScreen />} />
      <Route path="/payment-breakdown" element={<PaymentBreakdownScreen />} />
      <Route path="/payment-success" element={<PaymentSuccessScreen />} />
      <Route path="/payment-success-options" element={<PaymentSuccessMailOptionScreen />} />
      <Route path="/thank-you-goodbye" element={<ThankYouGoodbyeScreen />} />

      {/* Super Admin Routes - PROTECTED ✅ */}
      <Route 
        path="/super-admin" 
        element={
          <PrivateRoute allowedRoles={['super_admin']}>
            <SuperAdminLayout />
          </PrivateRoute>
        }
      >
        <Route index element={<SuperAdminDashboard />} />
        <Route path="restaurants/:id" element={<RestaurantDetailScreen />} />
        <Route path="restaurants/:id/statistics" element={<RestaurantStatisticsScreen />} />
        <Route path="restaurants/:id/transactions" element={<RestaurantTransactionsScreen />} />
        <Route path="restaurants" element={<RestaurantListScreen />} />
        <Route path="create" element={<CreateRestaurantScreen />} />
        <Route path="migration" element={<MigrationToolScreen />} />
      </Route>

      {/* Regular Admin Routes */}
      <Route 
        path="/admin" 
        element={
          <PrivateRoute allowedRoles={['owner', 'admin']}>
            <AdminLayout />
          </PrivateRoute>
        }
      >
        <Route index element={<AdminDashboardHome />} />
        <Route path="settings" element={<SettingScreen />} />
        <Route path="pos-config" element={<POSConfigScreen />} />
        <Route path="mesas" element={<AdminMesasScreen />} />
        <Route path="mesa/:id/resumen" element={<MesaSummaryScreen />} />
        <Route path="mesa/:id/transactions" element={<MesaTransactionsScreen />} />
        <Route path="mesa/:id/qr" element={<MesaQRCodeScreen />} />
        <Route path="qrcodes" element={<QRCodesScreen />} />
        <Route path="pagos" element={<PagosScreen />} />
        <Route path="migrate" element={<MigrateMesas />} />
      </Route>

      {/* Catch-all route - redirect to appropriate default route based on role */}
      <Route path="*" element={<Navigate to={getDefaultRouteForRole(userRole)} replace />} />
    </Routes>
  );
};

export default AppRoutes;